/**
 * Bracket stage component for A vs B matchups.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Swords, Trophy } from 'lucide-react';
import { useCallback } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { CountryCode } from '@/data/countries/types';
import {
  getDecidedMatchesInRound,
  getMatchesInRound,
  getRoundName,
} from '@/lib/tournament';
import { MatchCard } from './MatchCard';
import { useTournament } from './TournamentProvider';

export function BracketStage() {
  const { state, currentMatch, progress, actions } = useTournament();

  const handleSelect = useCallback(
    (winner: CountryCode) => {
      actions.submitMatchResult(winner);
    },
    [actions],
  );

  if (!currentMatch || !state?.bracket) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">No match to decide</p>
      </div>
    );
  }

  const totalRounds = Math.max(...state.bracket.map((m) => m.round));
  const roundName = getRoundName(
    currentMatch.round,
    totalRounds,
    currentMatch.isThirdPlace,
  );
  const isThirdPlace = currentMatch.isThirdPlace ?? false;
  const isFinal = currentMatch.round === totalRounds && !isThirdPlace;

  // Get match count within current round (not total bracket)
  const matchesInRound = isThirdPlace
    ? 1
    : getMatchesInRound(state.bracket, currentMatch.round).length;
  const decidedInRound = isThirdPlace
    ? 0
    : getDecidedMatchesInRound(state.bracket, currentMatch.round);
  const currentMatchInRound = decidedInRound + 1;

  // Skip auto-decided BYE matches
  if (currentMatch.countryA === currentMatch.countryB) {
    // This shouldn't happen in normal flow, but handle gracefully
    return null;
  }

  return (
    <div className="h-full flex flex-col">
      <Card className="flex-1 flex flex-col overflow-hidden">
        <CardHeader className="shrink-0 text-center pb-2">
          <CardTitle className="flex items-center justify-center gap-2 text-xl lg:text-2xl">
            {isFinal ? (
              <Trophy className="w-6 h-6 text-yellow-500" />
            ) : isThirdPlace ? (
              <span className="text-2xl">🥉</span>
            ) : (
              <Swords className="w-6 h-6 text-primary" />
            )}
            {roundName}
          </CardTitle>
          <CardDescription>
            {matchesInRound > 1
              ? `Match ${currentMatchInRound} of ${matchesInRound}`
              : 'Pick the country you prefer'}
          </CardDescription>
        </CardHeader>

        {/* Main content - flex layout distributes space evenly between cards */}
        <CardContent className="flex-1 min-h-0 flex flex-col p-3 lg:p-6 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentMatch.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full flex flex-col lg:flex-row items-stretch gap-2 lg:gap-6"
            >
              {/* Country A - flex-1 to share available space equally */}
              <div className="flex-1 min-h-0 lg:min-w-0">
                <MatchCard
                  code={currentMatch.countryA}
                  onSelect={() => handleSelect(currentMatch.countryA)}
                  disabled={currentMatch.winner !== null}
                  isWinner={currentMatch.winner === currentMatch.countryA}
                />
              </div>

              {/* VS Divider - compact sizing */}
              <div className="shrink-0 flex items-center justify-center">
                <div className="flex items-center justify-center w-10 h-10 lg:w-14 lg:h-14 rounded-full bg-muted border-2 border-border">
                  <span className="font-display text-sm lg:text-lg font-bold text-muted-foreground">
                    VS
                  </span>
                </div>
              </div>

              {/* Country B - flex-1 to share available space equally */}
              <div className="flex-1 min-h-0 lg:min-w-0">
                <MatchCard
                  code={currentMatch.countryB}
                  onSelect={() => handleSelect(currentMatch.countryB)}
                  disabled={currentMatch.winner !== null}
                  isWinner={currentMatch.winner === currentMatch.countryB}
                />
              </div>
            </motion.div>
          </AnimatePresence>
        </CardContent>

        {/* Keyboard hint - compact on mobile */}
        <div className="shrink-0 px-4 py-2 lg:py-3 border-t text-center">
          <p className="text-xs text-muted-foreground">
            Tap a country to select
          </p>
        </div>
      </Card>
    </div>
  );
}
